/**
 * @fileoverview Syndicator Showdown — Shareable Stable Summary card
 * @module components/share/ShareStableCard
 *
 * Reusable, presentational "premium public stable card" — used by:
 *   - the public /share/stable/:shareId page (header)
 *   - the Share QR modal preview (My Stable → Share My Stable)
 *
 * Renders over premium dark artwork (web/public/stable-share-dark.png) with
 * a dark overlay for readability. All text is real React/HTML — never baked
 * into the image — so it stays crisp, accessible, and easy to keep in sync
 * with live data.
 */

import React from 'react';
import { getShareArtwork } from '../../utils/shareArtwork';
import { DEFAULT_PROFILE_IMAGE } from '../../utils/profileImage';
import './ShareStableCard.css';

function fmt$(n) {
  if (!n && n !== 0) return '—';
  return '$' + Math.round(n).toLocaleString('en-AU');
}

function ordinal(n) {
  if (n === null || n === undefined) return null;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

export default function ShareStableCard({
  avatarUrl,
  stableName,
  showdownName,
  statusLabel,
  resultedRaceCount = 0,
  totalRaceCount = 0,
  virtualWinnings = 0,
  rank = null,
  totalRunners = null,
  artworkType = 'stable',
  actions = null,
  compact = false,
}) {
  const showProgress = totalRaceCount > 0;

  return (
    <div
      className={`ssc-card ${compact ? 'ssc-card--compact' : ''}`}
      style={{ backgroundImage: `url(${getShareArtwork(artworkType)})` }}
    >
      <div className="ssc-overlay" />

      <div className="ssc-content">
        <div className="ssc-owner-row">
          <img
            src={avatarUrl || DEFAULT_PROFILE_IMAGE}
            alt=""
            className="ssc-avatar"
            onError={(e) => { e.currentTarget.src = DEFAULT_PROFILE_IMAGE; }}
          />
          <div className="ssc-owner-text">
            <span className="ssc-stable-name">{stableName || 'A Syndicator Showdown Player'}’s Stable</span>
            {showdownName && <span className="ssc-showdown-name">{showdownName}</span>}
          </div>
          {statusLabel && <span className="ssc-status-pill">{statusLabel}</span>}
        </div>

        {showProgress && (
          <div className="ssc-progress-row">
            <div className="ssc-progress-track">
              <div
                className="ssc-progress-fill"
                style={{ width: `${Math.min(100, Math.round((resultedRaceCount / totalRaceCount) * 100))}%` }}
              />
            </div>
            <span className="ssc-progress-label">{resultedRaceCount} / {totalRaceCount} races resulted</span>
          </div>
        )}

        <div className="ssc-stats-row">
          <div className="ssc-stat">
            <span className="ssc-stat-val">{fmt$(virtualWinnings)}</span>
            <span className="ssc-stat-label">Virtual Winnings</span>
          </div>
          {rank != null && (
            <div className="ssc-stat">
              <span className="ssc-stat-val ssc-stat-val--gold">{ordinal(rank)}</span>
              <span className="ssc-stat-label">Current Rank</span>
            </div>
          )}
          {totalRunners != null && (
            <div className="ssc-stat">
              <span className="ssc-stat-val">{totalRunners}</span>
              <span className="ssc-stat-label">Runners Selected</span>
            </div>
          )}
        </div>

        {actions && <div className="ssc-actions">{actions}</div>}
      </div>
    </div>
  );
}
